import React, { useState, useEffect } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import InputGroup from "react-bootstrap/InputGroup";
import { ChevronLeft, ChevronRight, Download, Upload, GraphUpArrow } from "react-bootstrap-icons";

import formatCurrency from "../helpers/currencyHelper";

import LineChart from "../components/charts/LineChart";

import IncomesReports from "./reports/IncomesReports";
import InvoicesReports from "./reports/InvoicesReports";
import ExpensesReports from "./reports/ExpensesReports";
import ExpensesProjectsReports from "./reports/ExpensesProjectsReports";
import PayrollsReports from "./reports/PayrollsReports";
import CashFlowReport from "./reports/CashFlowReport";
import { MAX_YEAR, MIN_YEAR } from "../config/calendar";
import { Button } from "react-bootstrap";

const Reports = () => {
  const intl = useIntl();
  const currentYear = new Date().getFullYear();
  const [incomes, setIncomes] = useState(null);
  const [incomesDataset, setIncomesDataset] = useState(null);
  const [incomesTotalAmount, setIncomesTotalAmount] = useState(0);
  const [invoicesDataset, setInvoicesDataset] = useState(null);
  const [invoicesTotalAmount, setInvoicesTotalAmount] = useState(0);
  const [incomesTotal, setIncomesTotal] = useState(0);
  const [expenses, setExpenses] = useState(null);
  const [expensesDataset, setExpensesDataset] = useState(null);
  const [expensesTotalAmount, setExpensesTotalAmount] = useState(0);
  const [expensesProjectsDataset, setExpensesProjectsDataset] = useState(null);
  const [expensesProjectsTotalAmount, setExpensesProjectsTotalAmount] =
    useState(0);
  const [payrollsDataset, setPayrollsDataset] = useState(null);
  const [payrollsTotalAmount, setPayrollsTotalAmount] = useState(0);
  const [expensesTotal, setExpensesTotal] = useState(0);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const handleYearPrevious = () => {
    if (selectedYear > MIN_YEAR) {
      setSelectedYear(selectedYear - 1);
    }
  };

  const handleYearNext = () => {
    if (selectedYear < MAX_YEAR) {
      setSelectedYear(selectedYear + 1);
    }
  };

  const processDatasets = (datasets, labels) => {
    // Validar entrada
    if (!Array.isArray(datasets) || datasets.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }
    if (!Array.isArray(labels) || labels.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    // Paso 1: Ordenar etiquetas de forma cronológica
    const mergedLabels = [...new Set(labels)].sort(
      (a, b) => new Date(a) - new Date(b)
    );

    // Paso 2: Inicializar estructura para los importes consolidados
    const totalImporteDataset = {
      label: "Importe total consolidado",
      data: [],
      borderColor: "rgb(75, 192, 192)",
      backgroundColor: "rgba(75, 192, 192, 0.2)",
      yAxisID: "y",
    };

    // Paso 3: Iterar sobre datasets y calcular valores acumulados para cada fecha
    const runningTotals = Array(mergedLabels.length).fill(0); // Inicializar acumuladores por fecha
    datasets.forEach((dataset) => {
      mergedLabels.forEach((label, labelIndex) => {
        const labelPosition = labels.indexOf(label);
        const currentValue =
          labelPosition !== -1 ? dataset.data[labelPosition] || 0 : 0;
        runningTotals[labelIndex] += currentValue; // Sumar el valor actual al acumulador
      });
    });

    // Convertir los acumuladores en datos acumulativos
    let previousTotal = 0;
    totalImporteDataset.data = runningTotals.map((currentValue) => {
      const cumulativeValue = previousTotal + currentValue;
      previousTotal = cumulativeValue; // Actualizar el acumulador previo
      return cumulativeValue;
    });

    return {
      labels: mergedLabels,
      datasets: [totalImporteDataset],
    };
  };

  // Función para fusionar múltiples datasets y procesarlos
  const mergeMultipleDatasets = (datasets) => {
    if (!Array.isArray(datasets) || datasets.length === 0) {
      throw new Error("El arreglo de datasets no es válido.");
    }

    // Paso 1: Fusionar todas las etiquetas
    const mergedLabels = Array.from(
      new Set(
        datasets.flatMap((dataset) =>
          Array.isArray(dataset.labels) ? dataset.labels : []
        )
      )
    ).sort((a, b) => new Date(a) - new Date(b)); // Ordenar por fecha

    // Paso 2: Fusionar todos los datasets
    const mergedDatasets = datasets.flatMap((dataset) =>
      Array.isArray(dataset.datasets)
        ? dataset.datasets.map((ds) => ({
          ...ds,
          data: mergedLabels.map((label) =>
            Array.isArray(dataset.labels) && dataset.labels.includes(label)
              ? ds.data[dataset.labels.indexOf(label)] || 0
              : 0
          ),
        }))
        : []
    );

    return processDatasets(mergedDatasets, mergedLabels);
    /*return {
            labels: mergedLabels,
            datasets: mergedDatasets,
        }*/
  };

  const handleIncomesDataset = (dataSet) => {
    setIncomesDataset(dataSet);
    setIncomesTotalAmount(dataSet.totalAmountsSum);
    setIncomesTotal((prevTotal) => prevTotal + dataSet.totalAmountsSum);
  };

  const handleInvoicesDataset = (dataSet) => {
    setInvoicesDataset(dataSet);
    setInvoicesTotalAmount(dataSet.totalAmountsSum);
    setIncomesTotal((prevTotal) => prevTotal + dataSet.totalAmountsSum);
  };

  const handleExpensesDataset = (dataSet) => {
    setExpensesDataset(dataSet);
    setExpensesTotalAmount(dataSet.totalAmountsSum);
    setExpensesTotal((prevTotal) => prevTotal + dataSet.totalAmountsSum);
  };

  const handleExpensesProjectsDataset = (dataSet) => {
    setExpensesProjectsDataset(dataSet);
    setExpensesProjectsTotalAmount(dataSet.totalAmountsSum);
    setExpensesTotal((prevTotal) => prevTotal + dataSet.totalAmountsSum);
  };

  const handlePayrollsDataset = (dataSet) => {
    setPayrollsDataset(dataSet);
    setPayrollsTotalAmount(dataSet.totalAmountsSum);
    setExpensesTotal((prevTotal) => prevTotal + dataSet.totalAmountsSum);
  };

  useEffect(() => {
    setIncomesTotal(0);
    setExpensesTotal(0);
  }, [selectedYear]);

  // Re-fetch datasets on incomesDataset or invoicesDataset changes
  useEffect(() => {
    if (incomesDataset === null || invoicesDataset === null) return;

    setIncomes(mergeMultipleDatasets([incomesDataset, invoicesDataset]));
  }, [incomesDataset, invoicesDataset]);

  // Re-fetch datasets on expensesDataset or expensesProjectsDataset or payrollsDataset changes
  useEffect(() => {
    if (
      expensesDataset === null ||
      expensesProjectsDataset === null ||
      payrollsDataset === null
    )
      return;

    setExpenses(
      mergeMultipleDatasets([
        expensesDataset,
        expensesProjectsDataset,
        payrollsDataset,
      ])
    );
  }, [expensesDataset, expensesProjectsDataset, payrollsDataset]);

  return (
    <Container className="p-3" fluid>
      <Row className="mb-4 justify-content-center">
        <Col xs="auto">
          <InputGroup>
            <Button
              variant="outline-secondary"
              onClick={handleYearPrevious}
              disabled={selectedYear <= MIN_YEAR}
            >
              <ChevronLeft />
            </Button>
            <InputGroup.Text className="bg-white border-start-0 border-end-0 fw-bold">
              {selectedYear}
            </InputGroup.Text>
            <Button
              variant="outline-secondary"
              onClick={handleYearNext}
              disabled={selectedYear >= MAX_YEAR}
            >
              <ChevronRight />
            </Button>
          </InputGroup>
        </Col>
      </Row>

      <Row className="mb-4 g-5">
        <Col xs={12} lg={6} className="mb-4 mb-lg-0">
          <Card className="shadow-sm border-1" bg="warning">
            <Card.Header className="d-flex justify-content-between align-items-center border-0 pb-0" bg="warning">
              <div className="d-flex align-items-center fw-bold">
                <Download
                  className="text-success me-2"
                  size={20}
                  strokeWidth={4.5}
                />
                <span className="fs-5 fw-bold">
                  <FormattedMessage
                    id="reportsConsolidatedIncomes"
                    defaultMessage="Consolidated Incomes"
                  />
                </span>
              </div>
              <div className="fs-5 fw-bold text-success">
                {formatCurrency(incomesTotal)}
              </div>
            </Card.Header>
            <Card.Body className="pt-2">
              <hr className="my-2 border border-secondary" />
              <div className="p-3 rounded bg-light">
                {incomes && (
                  <LineChart
                    //title={intl.formatMessage({ id: "navInvoices", defaultMessage: "Customers Invoices" })}
                    data={incomes}
                  />
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={6} className="mb-4 mb-lg-0">
          <Card className="shadow-sm border-1" bg="warning">
            <Card.Header className="d-flex justify-content-between align-items-center border-0 pb-0" bg="warning">
              <div className="d-flex align-items-center fw-bold">
                <Upload
                  className="text-danger me-2"
                  size={20}
                  strokeWidth={4.5}
                />
                <span className="fs-5 fw-bold">
                  <FormattedMessage
                    id="reportsConsolidatedExpenses"
                    defaultMessage="Consolidated Expenses"
                  />
                </span>
              </div>
              <div className="fs-5 fw-bold text-danger">
                {formatCurrency(expensesTotal)}
              </div>
            </Card.Header>
            <Card.Body className="pt-2">
              <hr className="my-2 border border-secondary" />
              <div className="p-3 rounded bg-light">
                {expenses && (
                  <LineChart
                    //title={intl.formatMessage({ id: "navInvoices", defaultMessage: "Customers Invoices" })}
                    data={expenses}
                  />
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4 g-5">
        <Col xs={12} lg={6} className="mb-4 mb-lg-0">
          <Card className="shadow-sm border-1">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center border-0 pb-0">
              <div className="d-flex align-items-center fw-bold">
                <Download
                  className="text-success me-2"
                  size={20}
                  strokeWidth={4.5}
                />
                <span className="fs-5 fw-bold">
                  <FormattedMessage
                    id="navInvoices"
                    defaultMessage="Customers Invoices"
                  />
                </span>
              </div>
              <div className="fs-5 fw-bold text-success">
                {formatCurrency(invoicesTotalAmount)}
              </div>
            </Card.Header>
            <Card.Body className="pt-2">
              <hr className="my-2 border border-secondary" />
              <div className="p-3 rounded bg-light">
                <InvoicesReports
                  onChangeDataset={handleInvoicesDataset}
                  yearFrom={selectedYear}
                  yearTo={selectedYear}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={6} className="mb-4 mb-lg-0">
          <Card className="shadow-sm border-1">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center border-0 pb-0">
              <div className="d-flex align-items-center fw-bold">
                <Upload
                  className="text-danger me-2"
                  size={20}
                  strokeWidth={4.5}
                />
                <span className="fs-5 fw-bold">
                  <FormattedMessage
                    id="navPayrolls"
                    defaultMessage="Payrolls"
                  />
                </span>
              </div>
              <div className="fs-5 fw-bold text-danger">
                {formatCurrency(payrollsTotalAmount)}
              </div>
            </Card.Header>
            <Card.Body className="pt-2">
              <hr className="my-2 border border-secondary" />
              <div className="p-3 rounded bg-light">
                <PayrollsReports
                  onChangeDataset={handlePayrollsDataset}
                  yearFrom={selectedYear}
                  yearTo={selectedYear}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4 g-5">
        <Col xs={12} lg={6} className="mb-4 mb-lg-0">
          <Card className="shadow-sm border-1">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center border-0 pb-0">
              <div className="d-flex align-items-center fw-bold">
                <Download
                  className="text-success me-2"
                  size={20}
                  strokeWidth={4.5}
                />
                <span className="fs-5 fw-bold">
                  <FormattedMessage
                    id="navProjectIncomes"
                    defaultMessage="Projects Income"
                  />
                </span>
              </div>
              <div className="fs-5 fw-bold text-success">
                {formatCurrency(incomesTotalAmount)}
              </div>
            </Card.Header>
            <Card.Body className="pt-2">
              <hr className="my-2 border border-secondary" />
              <div className="p-3 rounded bg-light">
                <IncomesReports
                  onChangeDataset={handleIncomesDataset}
                  yearFrom={selectedYear}
                  yearTo={selectedYear}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6} lg={6}>
          <Card className="shadow-sm border-1">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center border-0 pb-0">
              <div className="d-flex align-items-center fw-bold">
                <Upload
                  className="text-danger me-2"
                  size={20}
                  strokeWidth={4.5}
                />
                <span className="fs-5 fw-bold">
                  <FormattedMessage
                    id="navExpensesGeneral"
                    defaultMessage="General Expenses"
                  />
                </span>
              </div>
              <div className="fs-5 fw-bold text-danger">
                {formatCurrency(expensesTotalAmount)}
              </div>
            </Card.Header>
            <Card.Body className="pt-2">
              <hr className="my-2 border border-secondary" />
              <div className="p-3 rounded bg-light">
                <ExpensesReports
                  onChangeDataset={handleExpensesDataset}
                  yearFrom={selectedYear}
                  yearTo={selectedYear}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="p-1">
        <Col xs={6} lg={6}></Col>
        <Col xs={6} lg={6}>
          <Card className="shadow-sm border-1">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center border-0 pb-0">
              <div className="d-flex align-items-center fw-bold">
                <Upload
                  className="text-danger me-2"
                  size={20}
                  strokeWidth={4.5}
                />
                <span className="fs-5 fw-bold">
                  <FormattedMessage
                    id="navExpensesProjects"
                    defaultMessage="Projects Expenses"
                  />
                </span>
              </div>
              <div className="fs-5 fw-bold text-danger">
                {formatCurrency(expensesProjectsTotalAmount)}
              </div>
            </Card.Header>
            <Card.Body className="pt-2">
              <hr className="my-2 border border-secondary" />
              <div className="p-3 rounded bg-light">
                <ExpensesProjectsReports
                  onChangeDataset={handleExpensesProjectsDataset}
                  yearFrom={selectedYear}
                  yearTo={selectedYear}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4 mt-5">
        <Col>
          <Card className="shadow-sm border-1">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center border-0 pb-0">
              <div className="d-flex align-items-center fw-bold">
                <GraphUpArrow
                  className="text-primary me-2"
                  size={20}
                  strokeWidth={4.5}
                />
                <span className="fs-5 fw-bold">
                  <FormattedMessage
                    id="cashFlow"
                    defaultMessage="Cash Flow"
                  />
                </span>
              </div>

              <div className="d-flex align-items-center">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleYearPrevious}
                  disabled={selectedYear <= MIN_YEAR}
                  className="border-0 p-1"
                >
                  <ChevronLeft size={16} />
                </Button>
                <span className="mx-2 fw-bold text-secondary">
                  {selectedYear}
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleYearNext}
                  disabled={selectedYear >= MAX_YEAR}
                  className="border-0 p-1"
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="pt-1 pb-2">
              <hr className="my-2 border border-secondary" />
              <div className="p-3 rounded bg-light">
                <CashFlowReport selectedYear={selectedYear} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

    </Container>
  );
};

export default Reports;
